import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, Loader2, Clock, User, ShieldCheck, Building2, AlertCircle } from "lucide-react";
import { getTicket, postTicketMessage, updateTicket } from "../api/department";
import { createTicketSocket, joinTicketRoom, leaveTicketRoom } from "../../src/realtime/ticketSocket";
import "../styles/department-base.css";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "ticket_assigned", label: "Ticket Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
];

const DEPARTMENT_LABELS = {
  legal: "Legal Department",
  medical: "Medical Department",
  education: "Education Department",
  emergency: "Emergency Support",
  finance: "Finance Department",
};

const formatAdminRole = (role) => {
  if (!role) return "Administrator";
  const normalized = String(role).trim().toLowerCase();
  if (normalized.includes("super")) return "Super Administrator";
  if (normalized.includes("ticket")) return "Ticket Administrator";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const getAdminRoleTone = (role) => {
  if (!role) return "default";
  const normalized = String(role).trim().toLowerCase();
  if (normalized.includes("super")) return "super";
  if (normalized.includes("ticket")) return "ticket";
  return "default";
};

export default function DepartmentTicketDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get("id");

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [conversationShellHeight, setConversationShellHeight] = useState(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const detailPanelRef = useRef(null);

  useEffect(() => {
    if (!ticketId) {
      navigate("/departments/tickets", { replace: true });
      return;
    }
    fetchTicket();
  }, [ticketId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    const activeTicketId = ticket?.id ?? ticketId;
    if (!activeTicketId) {
      return undefined;
    }

    const socket = createTicketSocket("department");
    if (!socket) {
      return undefined;
    }

    socketRef.current = socket;

    const handleReceiveMessage = (incomingMessage) => {
      if (String(incomingMessage?.ticketId ?? "") !== String(activeTicketId)) {
        return;
      }

      if (isDeptAssignment(incomingMessage) || isSystemAttachment(incomingMessage)) {
        return;
      }

      setMessages((previousMessages) => {
        const alreadyExists = previousMessages.some((message) => String(message.id) === String(incomingMessage.id));
        if (alreadyExists) {
          return previousMessages;
        }
        return [...previousMessages, incomingMessage];
      });
    };

    const joinActiveTicketRoom = () => {
      joinTicketRoom(socket, activeTicketId);
    };

    socket.on("connect", joinActiveTicketRoom);
    socket.on("receive_message", handleReceiveMessage);
    joinActiveTicketRoom();

    return () => {
      leaveTicketRoom(socket, activeTicketId);
      socket.off("connect", joinActiveTicketRoom);
      socket.off("receive_message", handleReceiveMessage);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [ticket?.id, ticketId]);

  useEffect(() => {
    const panelEl = detailPanelRef.current;
    if (!panelEl) {
      setConversationShellHeight(null);
      return undefined;
    }

    const updateConversationHeight = () => {
      if (window.innerWidth <= 1024) {
        setConversationShellHeight(null);
        return;
      }
      const panelHeight = panelEl.offsetHeight || 0;
      const viewportTarget = window.innerHeight - 220;
      const clampedViewportTarget = Math.max(520, Math.min(760, viewportTarget));
      setConversationShellHeight(Math.max(panelHeight, clampedViewportTarget));
    };

    updateConversationHeight();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateConversationHeight);
      resizeObserver.observe(panelEl);
    }

    window.addEventListener("resize", updateConversationHeight);

    return () => {
      window.removeEventListener("resize", updateConversationHeight);
      resizeObserver?.disconnect();
    };
  }, [ticket, error, updatingStatus]);

  const fetchTicket = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTicket(ticketId);
      setTicket(response.data.ticket);
      setMessages(response.data.messages || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await postTicketMessage(ticketId, { message: newMessage.trim() });
      setNewMessage("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      await updateTicket(ticketId, { status: newStatus });
      setTicket((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const isDeptAssignment = (msg) => {
    try {
      const parsed = typeof msg.message === "string" ? JSON.parse(msg.message) : null;
      return parsed?.type === "department-assignment";
    } catch {
      return false;
    }
  };

  const isSystemAttachment = (msg) => {
    return typeof msg.message === "string" && msg.message.startsWith("[System] Attached file:");
  };

  const getAuthorInfo = (msg) => {
    if (msg.authorDeptAccountId) {
      const departmentRole = msg.authorDeptDepartment
        ? DEPARTMENT_LABELS[msg.authorDeptDepartment] || msg.authorDeptDepartment
        : "Department";
      return {
        name: msg.authorDeptName || "Department Handler",
        role: departmentRole,
        type: "department",
      };
    }
    if (msg.authorAdminId) {
      return {
        name: msg.authorAdminName || "Admin",
        role: formatAdminRole(msg.authorAdminRole),
        adminRoleTone: getAdminRoleTone(msg.authorAdminRole),
        type: "admin",
      };
    }
    return {
      name: msg.authorUserName || "Member",
      role: null,
      adminRoleTone: null,
      type: "user",
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="dept-page">
        <div className="dept-loading">
          <Loader2 size={24} className="dept-spin" />
          <span>Loading ticket...</span>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="dept-page">
        <div className="dept-alert dept-alert--error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
        <button type="button" className="dept-btn dept-btn--ghost" onClick={() => navigate("/departments/tickets")}>
          <ArrowLeft size={16} /> Back to Tickets
        </button>
      </div>
    );
  }

  const isOverdue = ticket?.slaDeadline && new Date(ticket.slaDeadline) < new Date() && ["open", "ticket_assigned", "in_progress"].includes(ticket?.status);

  const visibleMessages = messages.filter((msg) => !isDeptAssignment(msg) && !isSystemAttachment(msg));
  const conversationShellStyle = conversationShellHeight
    ? { height: `${conversationShellHeight}px`, maxHeight: `${conversationShellHeight}px` }
    : undefined;

  return (
    <div className="dept-page dept-page--ticket-detail">
      <div className="dept-detail-header">
        <button type="button" className="dept-btn dept-btn--ghost" onClick={() => navigate("/departments/tickets")}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="dept-detail-header__info">
          <code className="dept-ticket-no">{ticket?.ticketNo}</code>
          <h1>{ticket?.subject}</h1>
        </div>
      </div>

      {error && (
        <div className="dept-alert dept-alert--error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="dept-detail-grid">
        {/* Left: Conversation */}
        <div className="dept-detail-conversation">
          <div className="dept-conversation-shell" style={conversationShellStyle}>
            <div className="dept-chat-box">
              <div className="dept-messages">
                {visibleMessages.map((msg) => {
                  const author = getAuthorInfo(msg);
                  const roleToneClass = author.type === "admin"
                    ? ` dept-message--admin-${author.adminRoleTone || "default"}`
                    : "";
                  return (
                    <div key={msg.id} className={`dept-message dept-message--${author.type}${roleToneClass}`}>
                      <div className="dept-message__avatar">
                        {author.type === "user" && <User size={16} />}
                        {author.type === "admin" && <ShieldCheck size={16} />}
                        {author.type === "department" && <Building2 size={16} />}
                      </div>
                      <div className="dept-message__body">
                        <div className="dept-message__header">
                          <span className="dept-message__author">{author.name}</span>
                          {author.role ? (
                            <span
                              className={`dept-message__author-role dept-message__author-role--${author.type}${
                                author.type === "admin"
                                  ? ` dept-message__author-role--admin-${author.adminRoleTone || "default"}`
                                  : ""
                              }`}
                            >
                              {author.role}
                            </span>
                          ) : null}
                          <span className="dept-message__time">{formatDate(msg.createdAt)}</span>
                        </div>
                        <div className="dept-message__text">{msg.message}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <form onSubmit={handleSendMessage} className="dept-reply-form">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your reply..."
                className="dept-reply-input"
                rows={3}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
              <button
                type="submit"
                className="dept-btn dept-btn--primary"
                disabled={!newMessage.trim() || sending}
              >
                {sending ? <Loader2 size={16} className="dept-spin" /> : <Send size={16} />}
                Send
              </button>
            </form>
          </div>
        </div>

        {/* Right: Ticket info panel */}
        <div className="dept-detail-panel" ref={detailPanelRef}>
          <div className="dept-panel-section">
            <h3>Ticket Details</h3>
            <div className="dept-panel-row">
              <span className="dept-panel-label">Category</span>
              <span className="dept-badge dept-badge--outline">{ticket?.category}</span>
            </div>
            <div className="dept-panel-row">
              <span className="dept-panel-label">Priority</span>
              <span className={`dept-badge ${ticket?.priority === "urgent" ? "dept-badge--red" : ticket?.priority === "high" ? "dept-badge--orange" : ticket?.priority === "medium" ? "dept-badge--yellow" : "dept-badge--gray"}`}>
                {ticket?.priority}
              </span>
            </div>
            <div className="dept-panel-row">
              <span className="dept-panel-label">Department</span>
              <span>{ticket?.department || "-"}</span>
            </div>
            <div className="dept-panel-row">
              <span className="dept-panel-label">Member</span>
              <span>{ticket?.userName || ticket?.userEmail || "-"}</span>
            </div>
            {ticket?.slaDeadline && (
              <div className="dept-panel-row">
                <span className="dept-panel-label">SLA Deadline</span>
                <span className={`dept-sla ${isOverdue ? "dept-sla--overdue" : ""}`}>
                  <Clock size={12} />
                  {formatDate(ticket.slaDeadline)}
                </span>
              </div>
            )}
          </div>

          <div className="dept-panel-section">
            <h3>Update Status</h3>
            <select
              className="dept-select"
              value={ticket?.status || ""}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updatingStatus}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
